import React, {useState, useEffect, useCallback, useMemo} from "react";
import axios from "axios";
import moment from "moment";
import {
  Search,
  Download,
  Trash2,
  Eye,
  Calendar,
  Filter,
  X,
  CheckCircle,
  Clock,
  DollarSign,
  Building2,
  Hash,
  FileText,
} from "lucide-react";
import InvoiceTable from "./administrative/explore/component_v2";

const ModernInvoiceTable = () => {
  const [invoices, setInvoices] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [loading, setLoading] = useState(true);

  // API Functions
  const getInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get("/api2/Getinvoices");
      setInvoices(response.data);
      console.log("Fetched Invoices:", response.data);
    } catch (error) {
      console.error("Error fetching invoices:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    getInvoices();
  }, [getInvoices]);

  // Utility Functions
  const formatCurrency = (amount, taxRate = 19) => {
    const amt = Number(amount);
    const rate = Number(taxRate);

    if (isNaN(amt) || isNaN(rate)) return "0,00 €";

    const taxTotal = (amt * rate) / 100;
    const total = amt + taxTotal;

    return (
      total.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + " €"
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("de-DE");
  };

  const formatDateTime = (dateString) => {
    return moment(dateString).format("DD.MM.YYYY HH:mm");
  };

  // Filter Logic
  const filteredInvoices = useMemo(() => {
  //  let filtered = [...invoices];

        // Start by excluding invoices that have an order_number
   let filtered = [...invoices].filter((inv) => {
      const on = inv.order_number;
      return on === undefined || on === null || String(on).trim() === "";
    });

    // Text filter
    if (filterText) {
      filtered = filtered.filter(
        (invoice) =>
          invoice.hosp_name?.toLowerCase().includes(filterText.toLowerCase()) ||
          invoice.invoice_number
            ?.toLowerCase()
            .includes(filterText.toLowerCase()) ||
          invoice.description?.toLowerCase().includes(filterText.toLowerCase())
      );
    }

    // Date filter
    if (startDate && endDate) {
      filtered = filtered.filter((invoice) => {
        const start = moment(startDate).startOf("day");
        const end = moment(endDate).endOf("day");
        const date = moment(invoice.created_at);
        return date.isBetween(start, end, "day", "[]");
      });
    }

    // Sorting
    if (sortField) {
      filtered.sort((a, b) => {
        let aVal = a[sortField];
        let bVal = b[sortField];

        if (sortField === "created_at" || sortField === "due_date") {
          aVal = new Date(aVal);
          bVal = new Date(bVal);
        }

        if (typeof aVal === "string") {
          aVal = aVal.toLowerCase();
          bVal = bVal.toLowerCase();
        }

        if (sortDirection === "asc") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    return filtered;
  }, [invoices, filterText, startDate, endDate, sortField, sortDirection]);

  // Pagination
  const totalPages = Math.ceil(filteredInvoices.length / itemsPerPage);
  const paginatedInvoices = filteredInvoices.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Event Handlers
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const handleRowSelect = (invoice) => {
    setSelectedRows((prev) => {
      const isSelected = prev.find(
        (row) => row.invoice_id === invoice.invoice_id
      );
      if (isSelected) {
        return prev.filter((row) => row.invoice_id !== invoice.invoice_id);
      } else {
        return [...prev, invoice];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedRows.length === paginatedInvoices.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows([...paginatedInvoices]);
    }
  };

  const handleStatusUpdate = async (id) => {
    try {
      await axios.post("/api2/UpdateInvoiceStatus", {id: id});
      setInvoices((prev) =>
        prev.map((invoice) => {
          if (invoice.invoice_id === id) {
            return {
              ...invoice,
              status: invoice.status === "Paid" ? "pending" : "Paid",
            };
          }
          return invoice;
        })
      );
    } catch (error) {
      console.error("Error updating invoice status:", error);
    }
  };

  const handleDelete = async (invoice) => {
    if (
      window.confirm(
        `Möchten Sie die Rechnung ${invoice.invoice_number} wirklich löschen?`
      )
    ) {
      try {
        await axios.post("/api2/delete_invoice", {id: invoice.invoice_id});
        setInvoices((prev) =>
          prev.filter((inv) => inv.invoice_id !== invoice.invoice_id)
        );
        setSelectedRows((prev) =>
          prev.filter((row) => row.invoice_id !== invoice.invoice_id)
        );
      } catch (error) {
        console.error("Error deleting invoice:", error);
      }
    }
  };

  const handleDownload = useCallback(async (filename) => {
    try {
      const response = await axios.get(
        `/api2/download-invoice?filename=${filename}`,
        {responseType: "blob"}
      );

      const blob = new Blob([response.data], {type: "application/pdf"});
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoice:", error);
    }
  }, []);

  const handleBulkDownload = async () => {
    try {
      const response = await axios.post(
        "/api2/download-invoices",
        {
          invoices: selectedRows.map((row) => row.invoice_number),
        },
        {responseType: "blob"}
      );

      const blob = new Blob([response.data], {type: "application/zip"});
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "invoices.zip";
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading invoices:", error);
    }
  };

  const clearFilters = () => {
    setFilterText("");
    setStartDate("");
    setEndDate("");
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.maxWidth}>
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <span style={styles.loadingText}>Lade Rechnungen...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="content">
      <div style={styles.container}>
        <div style={styles.maxWidth}>
          {/* Header */}
          <div style={styles.header}>
            <h1 style={styles.title}>Rechnungen</h1>
            <p style={styles.subtitle}>
              Verwalten Sie Ihre Rechnungen und Downloads
            </p>
          </div>

          {/* Filters Card */}
          <div style={styles.filtersCard}>
            <div style={styles.filtersContent}>
              <div style={styles.filtersHeader}>
                <Filter size={20} style={styles.filterIcon} />
                <h3 style={styles.filtersTitle}>Filter</h3>
                {(filterText || startDate || endDate) && (
                  <button onClick={clearFilters} style={styles.clearButton}>
                    <X size={16} />
                    Filter zurücksetzen
                  </button>
                )}
              </div>

              <div style={styles.filtersGrid}>
                <div style={styles.searchContainer}>
                  <Search size={16} style={styles.searchIcon} />
                  <input
                    type="text"
                    placeholder="Nach Kunde oder Nummer suchen..."
                    value={filterText}
                    onChange={(e) => setFilterText(e.target.value)}
                    style={styles.searchInput}
                  />
                </div>

                <div style={styles.dateContainer}>
                  <Calendar size={16} style={styles.dateIcon} />
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    style={styles.dateInput}
                  />
                </div>

                <div style={styles.dateContainer}>
                  <Calendar size={16} style={styles.dateIcon} />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    style={styles.dateInput}
                  />
                </div>

                {selectedRows.length > 0 && (
                  <button
                    onClick={handleBulkDownload}
                    style={styles.downloadButton}>
                    <Download size={16} />
                    {selectedRows.length} Download(s)
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Table Card */}
          <div style={styles.tableCard}>
            {/* Table Header with Stats */}
            <div style={styles.tableHeader}>
              <div style={styles.tableHeaderContent}>
                <div style={styles.tableStats}>
                  <span style={styles.invoiceCount}>
                    {filteredInvoices.length} Rechnung(en)
                  </span>
                  {selectedRows.length > 0 && (
                    <span style={styles.selectedCount}>
                      {selectedRows.length} ausgewählt
                    </span>
                  )}
                </div>
                <div style={styles.statusStats}>
                  <div style={styles.paidStat}>
                    <CheckCircle size={16} style={styles.paidIcon} />
                    {
                      invoices.filter((inv) => inv.status === "Paid").length
                    }{" "}
                    Bezahlt
                  </div>
                  <div style={styles.pendingStat}>
                    <Clock size={16} style={styles.pendingIcon} />
                    {
                      invoices.filter((inv) => inv.status === "pending").length
                    }{" "}
                    Offen
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead style={styles.tableHead}>
                  <tr>
                    <th style={styles.checkboxTh}>
                      <input
                        type="checkbox"
                        checked={
                          selectedRows.length === paginatedInvoices.length &&
                          paginatedInvoices.length > 0
                        }
                        onChange={handleSelectAll}
                        style={styles.checkbox}
                      />
                    </th>
                    <th
                      style={styles.sortableTh}
                      onClick={() => handleSort("invoice_id")}>
                      <div style={styles.thContent}>
                        <Hash size={16} />
                        ID
                      </div>
                    </th>
                    <th
                      style={styles.sortableTh}
                      onClick={() => handleSort("invoice_number")}>
                      <div style={styles.thContent}>
                        <FileText size={16} />
                        Nummer
                      </div>
                    </th>
                    <th
                      style={styles.sortableTh}
                      onClick={() => handleSort("hosp_name")}>
                      <div style={styles.thContent}>
                        <Building2 size={16} />
                        Unternehmen
                      </div>
                    </th>
                    <th style={styles.th}>Beschreibung</th>
                    <th
                      style={{...styles.sortableTh, textAlign: "right"}}
                      onClick={() => handleSort("amount")}>
                      <div
                        style={{
                          ...styles.thContent,
                          justifyContent: "flex-end",
                        }}>
                        <DollarSign size={16} />
                        Gesamt
                      </div>
                    </th>
                    <th
                      style={{...styles.sortableTh, textAlign: "center"}}
                      onClick={() => handleSort("due_date")}>
                      Fälligkeit
                    </th>
                    <th
                      style={{...styles.sortableTh, textAlign: "center"}}
                      onClick={() => handleSort("created_at")}>
                      Erstellt
                    </th>
                    <th style={{...styles.th, textAlign: "center"}}>Status</th>
                    <th style={{...styles.th, textAlign: "center"}}>
                      Aktionen
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedInvoices.map((invoice) => (
                    <tr
                      key={invoice.invoice_id}
                      style={{
                        ...styles.tableRow,
                        ...(selectedRows.find(
                          (row) => row.invoice_id === invoice.invoice_id
                        )
                          ? styles.selectedRow
                          : {}),
                      }}>
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={
                            !!selectedRows.find(
                              (row) => row.invoice_id === invoice.invoice_id
                            )
                          }
                          onChange={() => handleRowSelect(invoice)}
                          style={styles.checkbox}
                        />
                      </td>
                      <td style={styles.idTd}>{invoice.invoice_id}</td>
                      <td style={styles.numberTd}>{invoice.invoice_number}</td>
                      <td style={styles.td}>{invoice.hosp_name}</td>
                      <td style={styles.descriptionTd}>
                        {invoice.description}
                      </td>
                      <td style={styles.amountTd}>
                        {formatCurrency(invoice.amount)}
                      </td>
                      <td style={styles.dateTd}>
                        {formatDate(invoice.due_date)}
                      </td>
                      <td style={styles.dateTd}>
                        {formatDateTime(invoice.created_at)}
                      </td>
                      <td style={styles.statusTd}>
                        <span
                          style={
                            invoice.status === "Paid"
                              ? styles.paidBadge
                              : styles.pendingBadge
                          }>
                          {invoice.status === "Paid" ? (
                            <>
                              <CheckCircle size={12} style={styles.badgeIcon} />
                              Bezahlt
                            </>
                          ) : (
                            <>
                              <Clock size={12} style={styles.badgeIcon} />
                              Offen
                            </>
                          )}
                        </span>
                      </td>
                      <td style={styles.actionTd}>
                        <div style={styles.actionButtons}>
                          <button
                            onClick={() =>
                              handleDownload(invoice.invoice_number)
                            }
                            style={styles.iconButton}
                            title="Download">
                            <Download size={16} />
                          </button>
                          <button
                            onClick={() =>
                              handleStatusUpdate(invoice.invoice_id)
                            }
                            style={
                              invoice.status === "Paid"
                                ? styles.pendingStatusButton
                                : styles.paidStatusButton
                            }>
                            {invoice.status === "Paid"
                              ? "Als offen markieren"
                              : "Als bezahlt markieren"}
                          </button>
                          <button
                            onClick={() => handleDelete(invoice)}
                            style={styles.deleteButton}
                            title="Löschen">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div style={styles.paginationContainer}>
                <div style={styles.paginationContent}>
                  <div style={styles.paginationInfo}>
                    Zeige {(currentPage - 1) * itemsPerPage + 1} bis{" "}
                    {Math.min(
                      currentPage * itemsPerPage,
                      filteredInvoices.length
                    )}{" "}
                    von {filteredInvoices.length} Einträgen
                  </div>
                  <div style={styles.paginationButtons}>
                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(prev - 1, 1))
                      }
                      disabled={currentPage === 1}
                      style={{
                        ...styles.paginationButton,
                        ...(currentPage === 1 ? styles.disabledButton : {}),
                      }}>
                      Zurück
                    </button>

                    {Array.from({length: totalPages}, (_, i) => i + 1).map(
                      (page) => (
                        <button
                          key={page}
                          onClick={() => setCurrentPage(page)}
                          style={
                            currentPage === page
                              ? styles.activePaginationButton
                              : styles.paginationButton
                          }>
                          {page}
                        </button>
                      )
                    )}

                    <button
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                      }
                      disabled={currentPage === totalPages}
                      style={{
                        ...styles.paginationButton,
                        ...(currentPage === totalPages
                          ? styles.disabledButton
                          : {}),
                      }}>
                      Weiter
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {filteredInvoices.length === 0 && (
            <div style={styles.noData}>
              <FileText size={48} style={styles.noDataIcon} />
              <h3 style={styles.noDataTitle}>Keine Rechnungen gefunden</h3>
              <p style={styles.noDataText}>
                Versuchen Sie, Ihre Suchkriterien anzupassen.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    padding: "24px",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  maxWidth: {
    maxWidth: "1400px",
    margin: "0 auto",
  },
  loadingContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: "256px",
  },
  spinner: {
    width: "32px",
    height: "32px",
    border: "2px solid #e5e7eb",
    borderTop: "2px solid #2563eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
  },
  loadingText: {
    marginLeft: "12px",
    color: "#6b7280",
  },
  header: {
    marginBottom: "32px",
  },
  title: {
    fontSize: "30px",
    fontWeight: "bold",
    color: "#111827",
    margin: "0 0 8px 0",
  },
  subtitle: {
    color: "#6b7280",
    margin: 0,
  },
  filtersCard: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow:
      "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    border: "1px solid #e5e7eb",
    marginBottom: "24px",
  },
  filtersContent: {
    padding: "24px",
  },
  filtersHeader: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    marginBottom: "16px",
  },
  filterIcon: {
    color: "#6b7280",
  },
  filtersTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#111827",
    margin: 0,
  },
  clearButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "6px 12px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#6b7280",
    backgroundColor: "#f3f4f6",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  filtersGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "16px",
  },
  searchContainer: {
    position: "relative",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9ca3af",
  },
  searchInput: {
    width: "100%",
    paddingLeft: "40px",
    paddingRight: "16px",
    paddingTop: "8px",
    paddingBottom: "8px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
    outline: "none",
    transition: "all 0.2s",
  },
  dateContainer: {
    position: "relative",
  },
  dateIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9ca3af",
  },
  dateInput: {
    width: "100%",
    paddingLeft: "40px",
    paddingRight: "16px",
    paddingTop: "8px",
    paddingBottom: "8px",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontSize: "14px",
    outline: "none",
    transition: "all 0.2s",
  },
  downloadButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "8px 16px",
    backgroundColor: "#2563eb",
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  tableCard: {
    backgroundColor: "white",
    borderRadius: "8px",
    boxShadow:
      "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
    border: "1px solid #e5e7eb",
    overflow: "hidden",
  },
  tableHeader: {
    padding: "16px 24px",
    borderBottom: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
  },
  tableHeaderContent: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  tableStats: {
    display: "flex",
    alignItems: "center",
    gap: "24px",
  },
  invoiceCount: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#111827",
  },
  selectedCount: {
    fontSize: "14px",
    fontWeight: "500",
    color: "#2563eb",
  },
  statusStats: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    fontSize: "14px",
    color: "#6b7280",
  },
  paidStat: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  paidIcon: {
    color: "#10b981",
  },
  pendingStat: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
  },
  pendingIcon: {
    color: "#f59e0b",
  },
  tableWrapper: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHead: {
    backgroundColor: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
  },
  th: {
    padding: "12px 24px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "500",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },
  sortableTh: {
    padding: "12px 24px",
    textAlign: "left",
    fontSize: "12px",
    fontWeight: "500",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  checkboxTh: {
    padding: "12px 24px",
    textAlign: "left",
    width: "50px",
  },
  thContent: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  tableRow: {
    backgroundColor: "white",
    borderBottom: "1px solid #f3f4f6",
    transition: "background-color 0.2s",
  },
  selectedRow: {
    backgroundColor: "#eff6ff",
    borderLeft: "4px solid #2563eb",
  },
  td: {
    padding: "16px 24px",
    fontSize: "14px",
    color: "#111827",
  },
  idTd: {
    padding: "16px 24px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#111827",
  },
  numberTd: {
    padding: "16px 24px",
    fontSize: "14px",
    fontWeight: "500",
    color: "#2563eb",
  },
  descriptionTd: {
    padding: "16px 24px",
    fontSize: "14px",
    color: "#111827",
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  amountTd: {
    padding: "16px 24px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#111827",
    textAlign: "right",
  },
  dateTd: {
    padding: "16px 24px",
    fontSize: "14px",
    color: "#111827",
    textAlign: "center",
  },
  statusTd: {
    padding: "16px 24px",
    textAlign: "center",
  },
  paidBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 10px",
    backgroundColor: "#d1fae5",
    color: "#065f46",
    borderRadius: "9999px",
    fontSize: "12px",
    fontWeight: "500",
  },
  pendingBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "2px 10px",
    backgroundColor: "#fed7aa",
    color: "#9a3412",
    borderRadius: "9999px",
    fontSize: "12px",
    fontWeight: "500",
  },
  badgeIcon: {
    marginRight: "2px",
  },
  actionTd: {
    padding: "16px 24px",
    textAlign: "center",
  },
  actionButtons: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  iconButton: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px",
    color: "#6b7280",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  paidStatusButton: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: "500",
    color: "#065f46",
    backgroundColor: "#d1fae5",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  pendingStatusButton: {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 12px",
    fontSize: "12px",
    fontWeight: "500",
    color: "#9a3412",
  },
  deleteButton: {
    display: "inline-flex",
    alignItems: "center",
    padding: "6px",
    backgroundColor: "#fee2e2",
    color: "#991b1b",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  paginationContainer: {
    padding: "16px 24px",
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#f9fafb",
  },
  paginationContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "12px",
  },
  paginationInfo: {
    fontSize: "14px",
    color: "#6b7280",
  },
  paginationButtons: {
    display: "flex",
    gap: "8px",
  },
  paginationButton: {
    padding: "6px 12px",
    fontSize: "14px",
    color: "#2563eb",
    backgroundColor: "#ffffff",
    border: "1px solid #d1d5db",
    borderRadius: "4px",
    cursor: "pointer",
    transition: "all 0.2s",
  },
  activePaginationButton: {
    padding: "6px 12px",
    fontSize: "14px",
    color: "#ffffff",
    backgroundColor: "#2563eb",
    border: "1px solid #2563eb",
    borderRadius: "4px",
    cursor: "default",
  },
  disabledButton: {
    opacity: 0.5,
    cursor: "not-allowed",
  },
  noData: {
    textAlign: "center",
    padding: "48px 0",
    color: "#6b7280",
  },
  noDataIcon: {
    color: "#d1d5db",
    marginBottom: "16px",
  },
  noDataTitle: {
    fontSize: "20px",
    fontWeight: "600",
    marginBottom: "4px",
  },
  noDataText: {
    fontSize: "14px",
    color: "#9ca3af",
  },
};

export default ModernInvoiceTable;
